import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import AddMemoryCard from './AddMemoryCard';
import MemoryGarden from './MemoryGarden';
// import KeepsakeShop from './KeepsakeShop';
import Icon from './ui/Icon';
import PastelButton from './ui/PastelButton';
import './Memorial.css';

// Get time-of-day based tint (consistent with other premium pages)
const getTimeOfDayTint = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return 'from-sky-100/20 via-blue-50/10 to-cyan-50/15';
  } else if (hour >= 12 && hour < 17) {
    return 'from-amber-50/15 via-yellow-50/10 to-orange-50/15';
  } else if (hour >= 17 && hour < 21) {
    return 'from-orange-100/20 via-rose-50/15 to-pink-50/20';
  } else {
    return 'from-indigo-100/20 via-purple-50/15 to-violet-50/20';
  }
};

// Sparkle Layer Component for decorative sparkles
const SparkleLayer = ({ count = 5 }) => {
  const sparkles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${15 + Math.random() * 70}%`,
      delay: i * 1.2,
      duration: 7 + Math.random() * 4,
      size: Math.random() > 0.5 ? 'xs' : 'sm',
      color: ['#c084fc', '#f9a8d4', '#93c5fd', '#fcd34d'][i % 4],
    })), [count]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none motion-reduce:hidden" aria-hidden="true">
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className="absolute"
          style={{ left: sparkle.left, bottom: '-20px' }}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [0, -350, -500],
            opacity: [0, 0.5, 0],
            x: [0, Math.sin(sparkle.id) * 25, Math.sin(sparkle.id) * 40],
          }}
          transition={{
            duration: sparkle.duration,
            repeat: Infinity,
            delay: sparkle.delay,
            ease: 'easeOut',
          }}
        >
          <Icon name="sparkle" size={sparkle.size} color={sparkle.color} />
        </motion.div>
      ))}
    </div>
  );
};

const FloatingElement = ({ children, className, style }) => (
  <div className={`absolute pointer-events-none ${className}`} style={style} aria-hidden="true">
    {children}
  </div>
);

// Image compression utility
const compressImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.7) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedDataUrl);
    };
    
    if (typeof file === 'string') {
      img.src = file;
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  });
};

const Memorial = ({ petName, petId }) => {
  const [currentView, setCurrentView] = useState('garden'); // 'garden' or 'addMemory'
  const [memories, setMemories] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  const timeOfDayTint = useMemo(() => getTimeOfDayTint(), []);

  // Load memories from backend on component mount
  useEffect(() => {
    loadMemories();
  }, [petId]);

  // Fetch journal entries
  useEffect(() => {
    if (petId) {
      fetch(`${BACKEND_URL}/api/pets/${petId}/journal`)
        .then(res => res.json())
        .then(data => {
          setJournalEntries(data.entries || []);
        })
        .catch(err => console.error('Failed to fetch journal entries:', err));
    }
  }, [petId, BACKEND_URL]);

  const loadMemories = async () => {
    if (!petId) return;
    
    try {
      // PRIORITY 1: Load from localStorage IMMEDIATELY for instant UI
      const savedMemories = localStorage.getItem(`memories_${petId}`);
      if (savedMemories) {
        try {
          const parsedMemories = JSON.parse(savedMemories);
          setMemories(parsedMemories);
          console.log('⚡ Memories loaded instantly from cache:', parsedMemories.length);
          
          // Background sync from server (don't await)
          backgroundSyncMemories(petId);
          return;
        } catch (error) {
          console.error('Error parsing saved memories:', error);
          localStorage.removeItem(`memories_${petId}`);
        }
      }
      
      // PRIORITY 2: No cache - fetch from server
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories?limit=20`);
      if (response.ok) {
        const data = await response.json();
        const loadedMemories = data.memories || [];
        setMemories(loadedMemories);
        console.log('✅ Memories loaded from backend:', loadedMemories.length);
        
        // Cache for next instant load
        localStorage.setItem(`memories_${petId}`, JSON.stringify(loadedMemories));
      } else {
        console.error('Failed to load memories from backend');
      }
    } catch (error) {
      console.error('Error loading memories:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Background sync memories without blocking UI
  const backgroundSyncMemories = async (petIdToSync) => {
    try {
      console.log('[backgroundSync] 🔄 Syncing memories in background...');
      const response = await fetch(`${BACKEND_URL}/api/pets/${petIdToSync}/memories?limit=20`);
      if (response.ok) {
        const data = await response.json();
        const serverMemories = data.memories || [];
        
        // Update state and cache if server has different data
        const cachedStr = localStorage.getItem(`memories_${petIdToSync}`);
        const serverStr = JSON.stringify(serverMemories);
        
        if (cachedStr !== serverStr) {
          console.log('[backgroundSync] 🔄 Server has updated memories, refreshing...');
          setMemories(serverMemories);
          localStorage.setItem(`memories_${petIdToSync}`, serverStr);
        } else {
          console.log('[backgroundSync] ✅ Memories cache is valid');
        }
      }
    } catch (error) {
      console.warn('[backgroundSync] ⚠️ Background sync failed:', error.message);
    }
  };

  const handleSaveMemory = async (memory) => {
    try {
      setIsLoading(true);
      
      // Compress the image to reduce size
      const compressedImage = await compressImage(memory.image, 600, 400, 0.6);
      
      const compressedMemory = {
        ...memory,
        image: compressedImage
      };
      
      // Try to save to backend first
      try {
        const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(compressedMemory)
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Memory saved to backend:', data.message, 'ID:', data.memory_id);
          
          // Update local state with SERVER-RETURNED ID (important for delete to work!)
          const savedMemory = {
            ...compressedMemory,
            id: data.memory_id  // Use the server-generated UUID
          };
          const updatedMemories = [...memories, savedMemory];
          setMemories(updatedMemories);
          
          // Also save to localStorage as backup (with error handling)
          try {
            localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          } catch (storageError) {
            console.warn('⚠️ Could not save to localStorage (memory saved to backend successfully)');
          }
          
          // Navigate back to garden view
          setCurrentView('garden');
          return; // Success, exit early
        } else {
          throw new Error('Backend save failed');
        }
      } catch (backendError) {
        console.error('❌ Failed to save to backend:', backendError);
        
        // Fallback to localStorage only - generate a client-side ID
        const fallbackMemory = {
          ...compressedMemory,
          id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`  // Client-generated ID
        };
        const updatedMemories = [...memories, fallbackMemory];
        setMemories(updatedMemories);
        
        try {
          localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          console.log('✅ Memory saved to localStorage as fallback with ID:', fallbackMemory.id);
          // Navigate back to garden view
          setCurrentView('garden');
        } catch (storageError) {
          console.error('❌ Failed to save to localStorage:', storageError);
          throw new Error('Failed to save memory to both backend and localStorage. Please try with a smaller image.');
        }
      }
      
    } catch (error) {
      console.error('Error saving memory:', error);
      alert(error.message || 'Failed to save memory. Please try with a smaller image or try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMemory = async (updatedMemory) => {
    try {
      setIsLoading(true);
      
      // Compress the image to reduce size if it's a new image
      let finalMemory = updatedMemory;
      if (updatedMemory.image && !updatedMemory.image.startsWith('data:image/')) {
        // If it's not already a data URL, compress it
        const compressedImage = await compressImage(updatedMemory.image, 600, 400, 0.6);
        finalMemory = {
          ...updatedMemory,
          image: compressedImage
        };
      }
      
      // Find and update the memory in the array
      const updatedMemories = memories.map(memory => 
        memory.id === finalMemory.id || memory === finalMemory ? finalMemory : memory
      );
      
      // Try to update in backend first
      try {
        const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories/${finalMemory.id || finalMemory.date}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(finalMemory)
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Memory updated in backend:', data.message);
          
          // Update local state
          setMemories(updatedMemories);
          
          // Also save to localStorage as backup
          try {
            localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          } catch (storageError) {
            console.warn('⚠️ Could not save to localStorage (memory updated in backend successfully)');
          }
          
          return; // Success, exit early
        } else {
          throw new Error('Backend update failed');
        }
      } catch (backendError) {
        console.error('❌ Failed to update in backend:', backendError);
        
        // Fallback to localStorage only
        setMemories(updatedMemories);
        
        try {
          localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          console.log('✅ Memory updated in localStorage as fallback');
        } catch (storageError) {
          console.error('❌ Failed to update in localStorage:', storageError);
          throw new Error('Failed to update memory in both backend and localStorage.');
        }
      }
      
    } catch (error) {
      console.error('Error updating memory:', error);
      alert(error.message || 'Failed to update memory. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMemory = async (memoryToDelete) => {
    try {
      setIsLoading(true);
      
      // Remove from local state first for immediate UI feedback
      const updatedMemories = memories.filter(memory => {
        // Handle different ID formats - could be memory.id, memory.date, or object reference
        if (memory.id && memoryToDelete.id) {
          return memory.id !== memoryToDelete.id;
        } else if (memory.date && memoryToDelete.date) {
          return memory.date !== memoryToDelete.date;
        } else {
          // Fallback to object reference comparison
          return memory !== memoryToDelete;
        }
      });
      
      setMemories(updatedMemories);
      
      // Determine the delete ID
      const deleteId = memoryToDelete.id || memoryToDelete.date || memoryToDelete.timestamp;
      
      // Check if this is a local-only memory (starts with "local-")
      const isLocalOnly = deleteId && typeof deleteId === 'string' && deleteId.startsWith('local-');
      
      if (isLocalOnly) {
        // Local-only memory - just update localStorage, skip backend
        console.log('🗑️ Deleting local-only memory:', deleteId);
        try {
          localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          console.log('✅ Local memory deleted from localStorage');
        } catch (storageError) {
          console.error('❌ Failed to delete from localStorage:', storageError);
          setMemories(memories); // Restore original state
          throw new Error('Failed to delete memory from localStorage.');
        }
        return;
      }
      
      // Try to delete from backend
      try {
        console.log('🗑️ Deleting memory from backend with ID:', deleteId);
        const response = await fetch(`${BACKEND_URL}/api/pets/${petId}/memories/${encodeURIComponent(deleteId)}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Memory deleted from backend:', data.message);
          
          // Update localStorage as backup
          try {
            localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          } catch (storageError) {
            console.warn('⚠️ Could not update localStorage (memory deleted from backend successfully)');
          }
          
          return; // Success, exit early
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.warn('Backend delete returned:', response.status, errorData);
          throw new Error('Backend delete failed');
        }
      } catch (backendError) {
        console.error('❌ Failed to delete from backend:', backendError);
        
        // Even if backend fails, we still want to update localStorage
        // since we already updated the UI
        try {
          localStorage.setItem(`memories_${petId}`, JSON.stringify(updatedMemories));
          console.log('✅ Memory deleted from localStorage as fallback');
        } catch (storageError) {
          console.error('❌ Failed to delete from localStorage:', storageError);
          // In this case, we might want to restore the memory to the UI
          // since both backend and localStorage failed
          setMemories(memories); // Restore original state
          throw new Error('Failed to delete memory from both backend and localStorage.');
        }
      }
      
    } catch (error) {
      console.error('Error deleting memory:', error);
      alert(error.message || 'Failed to delete memory. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderMemoryGardenView = () => (
    <>
      {/* Memory Garden */}
      <div className="mb-4">
        <MemoryGarden memories={memories} onUpdateMemory={handleUpdateMemory} onDeleteMemory={handleDeleteMemory} />
      </div>

      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-gray-700 mb-5 flex items-center justify-center gap-2">
          <Icon name="sparkle" size="md" color="primary" animate />
          Memorial Garden
        </h1>
        
        {/* Add Memory Button with micro-interaction */}
        <motion.div
          whileHover={{ y: -2, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="inline-block"
        >
          <PastelButton
            onClick={() => setCurrentView('addMemory')}
            color="lavender"
            className="px-6 py-3 text-sm font-semibold flex items-center justify-center gap-2"
          >
            <Icon name="sparkle" size="sm" color="#4A90E2" animate />
            Add a Precious Memory
          </PastelButton>
        </motion.div>
      </motion.div>

      {/* Journal Entries Section - Premium Glass */}
      {journalEntries.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-xl mx-auto mb-8"
        >
          <div className="relative bg-white/20 backdrop-blur-md rounded-3xl px-1.5 sm:px-5 py-4 sm:py-6 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 overflow-hidden">
            {/* Inner highlight */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/25 via-white/10 to-transparent pointer-events-none" />
            
            <h3 className="relative text-lg font-semibold text-gray-700 mb-4 text-center flex items-center justify-center gap-2">
              <Icon name="heart" size="sm" color="memories" />
              Journal Memories
            </h3>
            <div className="relative space-y-3 max-h-64 overflow-y-auto">
              {journalEntries.slice(0, 3).map((entry, idx) => (
                <motion.div 
                  key={entry.id || idx} 
                  className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/40 hover:shadow-md transition-shadow duration-200"
                  whileHover={{ y: -1 }}
                >
                  <p className="text-gray-700 mb-2 line-clamp-2 text-sm leading-relaxed">{entry.content}</p>
                  <small className="text-gray-500 font-medium text-xs">
                    {new Date(entry.timestamp).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </small>
                </motion.div>
              ))}
            </div>
            {journalEntries.length > 3 && (
              <div className="relative text-center mt-4">
                <span className="text-gray-500 text-xs bg-white/30 px-3 py-1.5 rounded-full">
                  And {journalEntries.length - 3} more journal entries...
                </span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Keepsake Shop */}
      {/* <div className="mb-6">
        <KeepsakeShop petName={petName} />
      </div> */}
    </>
  );

  const renderAddMemoryView = () => (
    <>
      {/* Header with back button */}
      <div className="relative text-center mb-6 pt-10">
        <motion.button
          onClick={() => setCurrentView('garden')}
          className="absolute top-0 left-0 bg-white/70 backdrop-blur-md text-gray-600 px-4 py-2 rounded-xl font-medium border border-white/40 shadow-md text-sm"
          whileHover={{ y: -2, backgroundColor: 'rgba(255,255,255,0.85)' }}
          whileTap={{ scale: 0.98 }}
        >
          ← Back
        </motion.button>
        
        <h1 className="text-xl md:text-2xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2">
          <Icon name="sparkle" size="md" color="primary" animate />
          Add a Precious Memory
        </h1>
        <p className="text-sm text-gray-600 mt-1 max-w-sm mx-auto">
          Share a beautiful moment with Angel {petName || 'your beloved pet'}
        </p>
        <div className="w-20 h-0.5 bg-gradient-to-r from-pink-400/60 via-purple-400/60 to-blue-400/60 rounded-full mx-auto mt-3"></div>
      </div>

      {/* Add Memory Form - Responsive container */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-xl mx-auto pb-20 px-0.5 sm:px-4"
      >
        <AddMemoryCard onSaveMemory={handleSaveMemory} />
      </motion.div>
    </>
  );

  return (
    <div className="memorial-garden-bg heaven-background relative min-h-screen">
      {/* Grain/noise overlay for premium texture */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
        aria-hidden="true"
      />
      
      {/* Time-of-day tint overlay */}
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${timeOfDayTint} z-0`} aria-hidden="true" />
      
      {/* Radial glow behind hero content */}
      <div 
        className="absolute top-32 left-1/2 -translate-x-1/2 w-96 h-96 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.12) 0%, rgba(236, 72, 153, 0.06) 40%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      {/* Floating Stars - decorative only, low opacity */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
        {[...Array(8)].map((_, i) => (
          <FloatingElement
            key={`star-${i}`}
            className="animate-twinkle opacity-20"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
            }}
          >
            <Icon name="star" size="sm" color="gratitude" />
          </FloatingElement>
        ))}
      </div>

      {/* Floating Clouds - decorative only, low opacity */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
        {[...Array(4)].map((_, i) => (
          <FloatingElement
            key={`cloud-${i}`}
            className="animate-float-slow opacity-15"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${10 + Math.random() * 50}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${8 + Math.random() * 4}s`,
            }}
          >
            <Icon name="cloud" size="md" color="light" />
          </FloatingElement>
        ))}
      </div>

      {/* Loading Overlay - Premium glass style */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-white/85 backdrop-blur-md rounded-3xl p-8 text-center shadow-[0_16px_40px_rgba(0,0,0,0.12)] border border-white/50"
          >
            {/* Inner highlight */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-purple-200 border-t-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-700 font-medium">
                {currentView === 'addMemory' ? 'Saving your precious memory...' : 'Loading memories...'}
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Content Container */}
      <div className="container min-h-[calc(100vh-96px)] sm:p-4 relative z-10">
        {currentView === 'garden' ? renderMemoryGardenView() : renderAddMemoryView()}
      </div>
    </div>
  );
};

export default Memorial; 